import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * FormField — consistent label + input + help/error chrome used across
 * Onboarding, ReaderEdit, Account, and later the admin editor. The
 * wrapper owns the `htmlFor` wiring so pages don't have to invent ids.
 */
export interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> {
  label: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, help, error, required, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? `field-${autoId}`;
  const describedBy = error ? `${inputId}-error` : help ? `${inputId}-help` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={inputId}
        className="or-field-label text-[11px] font-semibold uppercase tracking-wider text-or-slate-600"
      >
        {label}
        {required ? <span className="ml-1 text-or-rose-500">*</span> : null}
      </label>
      <input
        id={inputId}
        ref={ref}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        className={cn(
          // Font is 16px on mobile, 14px from `sm` up. iOS Safari auto-zooms
          // into any focused input whose font-size is < 16px, and the zoomed
          // viewport then pans horizontally (the "screen moves left/right and
          // isn't stable" bug on the onboarding forms). 16px on phones
          // suppresses the zoom; the 14px design is preserved on larger screens.
          'rounded-xl border border-or-slate-200 bg-white px-3.5 py-2.5 text-[16px] text-or-slate-900 placeholder:text-or-slate-400 sm:text-[14px]',
          'focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30',
          error && 'border-or-rose-400 focus:border-or-rose-500 focus:ring-or-rose-500/30',
        )}
        {...rest}
      />
      {error ? (
        <span id={`${inputId}-error`} className="text-[12px] text-or-rose-600">
          {error}
        </span>
      ) : help ? (
        <span id={`${inputId}-help`} className="text-[12px] text-or-slate-500">
          {help}
        </span>
      ) : null}
    </div>
  );
});
